import { createReducer } from '@reduxjs/toolkit'

import * as groupActions from '../../actions/Groups/actions'
import * as keyMetricActions from '../../actions/KeyMetrics/actions'
import * as authenticationActions from '../../actions/Authentication/actions'

import { TGroupApi } from 'types/GlobalGroup'
import { TKeyMetricApi } from 'types/GlobalKeyMetric'

export interface GroupKeyMetricLinkerState {
  byGroupId: Record<string, string[]>
  byKeyMetricId: Record<string, string[]>
}

const INITIAL_STATE: GroupKeyMetricLinkerState = {
  byGroupId: {},
  byKeyMetricId: {}
}

function buildFromGroups(groups: TGroupApi[]): GroupKeyMetricLinkerState {
  const byGroupId: Record<string, string[]> = {}
  const byKeyMetricId: Record<string, string[]> = {}

  groups.forEach((group) => {
    byGroupId[group.id] = [...group.key_metrics]

    group.key_metrics.forEach((kmId) => {
      if (!byKeyMetricId[kmId]) {
        byKeyMetricId[kmId] = []
      }

      byKeyMetricId[kmId].push(group.id)
    })
  })

  return { byGroupId, byKeyMetricId }
}

function buildFromKeyMetrics(
  keyMetrics: TKeyMetricApi[]
): GroupKeyMetricLinkerState {
  const byGroupId: Record<string, string[]> = {}
  const byKeyMetricId: Record<string, string[]> = {}

  const processKeyMetric = (km: TKeyMetricApi) => {
    if (km.group_ids) {
      byKeyMetricId[km.id] = [...km.group_ids]

      km.group_ids.forEach((groupId) => {
        if (!byGroupId[groupId]) {
          byGroupId[groupId] = []
        }

        byGroupId[groupId].push(km.id)
      })
    }

    km.children?.forEach(processKeyMetric)
  }

  keyMetrics.forEach(processKeyMetric)

  return { byGroupId, byKeyMetricId }
}

function updateForKeyMetric(
  state: GroupKeyMetricLinkerState,
  km: TKeyMetricApi
): GroupKeyMetricLinkerState {
  if (!km.group_ids) return state

  const byKeyMetricId: Record<string, string[]> = Object.fromEntries(
    Object.entries(state.byKeyMetricId).map(([k, v]) => [k, [...v]])
  )
  const byGroupId: Record<string, string[]> = Object.fromEntries(
    Object.entries(state.byGroupId).map(([k, v]) => [k, [...v]])
  )

  // remove old entries for this key metric
  const oldGroupIds = byKeyMetricId[km.id] || []

  oldGroupIds.forEach((groupId) => {
    if (byGroupId[groupId]) {
      byGroupId[groupId] = byGroupId[groupId].filter(
        (id) => id !== km.id
      )
    }
  })

  // add new entries
  byKeyMetricId[km.id] = [...km.group_ids]

  km.group_ids.forEach((groupId) => {
    if (!byGroupId[groupId]) {
      byGroupId[groupId] = []
    }

    byGroupId[groupId].push(km.id)
  })

  return { byGroupId, byKeyMetricId }
}

function updateForGroup(
  state: GroupKeyMetricLinkerState,
  group: TGroupApi
): GroupKeyMetricLinkerState {
  const byKeyMetricId: Record<string, string[]> = Object.fromEntries(
    Object.entries(state.byKeyMetricId).map(([k, v]) => [k, [...v]])
  )
  const byGroupId: Record<string, string[]> = Object.fromEntries(
    Object.entries(state.byGroupId).map(([k, v]) => [k, [...v]])
  )

  // remove old entries for this group
  const oldKeyMetricIds = byGroupId[group.id] || []

  oldKeyMetricIds.forEach((kmId) => {
    if (byKeyMetricId[kmId]) {
      byKeyMetricId[kmId] = byKeyMetricId[kmId].filter(
        (id) => id !== group.id
      )
    }
  })

  // add new entries
  byGroupId[group.id] = [...group.key_metrics]

  group.key_metrics.forEach((kmId) => {
    if (!byKeyMetricId[kmId]) {
      byKeyMetricId[kmId] = []
    }

    byKeyMetricId[kmId].push(group.id)
  })

  return { byGroupId, byKeyMetricId }
}

const groupKeyMetricLinkerReducer = createReducer(
  INITIAL_STATE,
  (builder) => {
    builder
      // groups fetched
      .addCase(groupActions.getAllSuccess, (_, action) => {
        return buildFromGroups(action.payload.data)
      })

      // key metrics fetched
      .addCase(keyMetricActions.getAllSuccess, (_, action) => {
        return buildFromKeyMetrics(action.payload)
      })

      // key metric updated
      .addCase(keyMetricActions.updateSuccess, (state, action) => {
        return updateForKeyMetric(state, action.payload)
      })

      // key metrics order updated (full re-fetch)
      .addCase(keyMetricActions.updateOrderSuccess, (_, action) => {
        return buildFromKeyMetrics(action.payload)
      })

      // pin actions (full re-fetch)
      .addCase(keyMetricActions.pinSuccess, (_, action) => {
        return buildFromKeyMetrics(action.payload)
      })

      // group add/remove key metric
      .addCase(groupActions.addKeyMetricSuccess, (state, action) => {
        return updateForGroup(state, action.payload.data)
      })
      .addCase(
        groupActions.removeKeyMetricSuccess,
        (state, action) => {
          return updateForGroup(state, action.payload.data)
        }
      )

      // sign out
      .addCase(authenticationActions.signOut, () => INITIAL_STATE)
      .addDefaultCase((state) => state)
  }
)

export default groupKeyMetricLinkerReducer
